import { fabric } from "fabric";
import { v4 as uuidv4 } from "uuid";

import { CustomFabricObject } from "../types/type";
import { TO_JSON_ADDITIONAL_PROPERTIES } from "../constants";
import { CurvedText } from "./curved-text";

export const handleCopy = (canvas: fabric.Canvas) => {
  const activeObjects = canvas.getActiveObjects();
  if (activeObjects.length > 0) {
    // Serialize the selected objects
    const serializedObjects = activeObjects.map((obj) =>
      obj.toObject(TO_JSON_ADDITIONAL_PROPERTIES)
    );
    // Store the serialized objects in the clipboard
    localStorage.setItem("clipboard", JSON.stringify(serializedObjects));
  }

  return activeObjects;
};

export function updateObjectIdsRecursively(obj: any) {
  // Check if the current object is a group
  if (obj.type === "group" && Array.isArray(obj._objects)) {
    // Iterate over the objects in the group
    obj._objects.forEach((child: any) => {
      // Recursively update objectId for child objects
      updateObjectIdsRecursively(child);
    });
  }

  // Assign a new objectId to the current object
  obj.objectId = uuidv4();
}

export const handlePaste = (
  canvas: fabric.Canvas,
  syncShapeInStorage: (shape: fabric.Object) => void
) => {
  if (!canvas || !(canvas instanceof fabric.Canvas)) {
    console.error("Invalid canvas object. Aborting paste operation.");
    return;
  }

  // Retrieve serialized objects from the clipboard
  const clipboardData = localStorage.getItem("clipboard");

  if (clipboardData) {
    try {
      const parsedObjects = JSON.parse(clipboardData);
      parsedObjects.forEach((objData: fabric.Object) => {
        // convert the plain javascript objects retrieved from localStorage into fabricjs objects (deserialization)
        fabric.util.enlivenObjects(
          [objData],
          (enlivenedObjects: any) => {
            enlivenedObjects.forEach((enlivenedObj: any) => {
              // if is curved text, we need to re-initialize it.

              // @ts-ignore
              if (enlivenedObj.type === "i-text" && enlivenedObj.isCurvedText) {
                const { text, ...rest } = enlivenedObj;

                const curvedText = new CurvedText(enlivenedObj.text, {
                  ...rest,
                });

                enlivenedObj = curvedText;
              }

              const copiedLeft = enlivenedObj.left ? enlivenedObj.left : 0;
              const copiedTop = enlivenedObj.top ? enlivenedObj.top : 0;
              // Offset the pasted objects to avoid overlap with existing objects
              enlivenedObj.set({
                left: copiedLeft,
                top: copiedTop,

                objectId: uuidv4(), // override the objectId to avoid conflicts with existing objects
              } as CustomFabricObject<any>);

              // if is group, override _objects objectId.
              if (enlivenedObj.type === "group") {
                enlivenedObj._objects.forEach((obj: any) => {
                  updateObjectIdsRecursively(enlivenedObj);
                });
              }

              canvas.add(enlivenedObj);
              syncShapeInStorage(enlivenedObj);
            });
            canvas.renderAll();
          },
          "fabric"
        );
      });
    } catch (error) {
      console.error("Error parsing clipboard data:", error);
    }
  }
};

export const handleDelete = (
  canvas: fabric.Canvas,
  deleteShapeFromStorage: (id: string) => void
) => {
  const activeObjects = canvas.getActiveObjects();
  if (!activeObjects || activeObjects.length === 0) return;

  if (activeObjects.length > 0) {
    activeObjects.forEach((obj: CustomFabricObject<any>) => {
      if (!obj.objectId) return;
      canvas.remove(obj);
      deleteShapeFromStorage(obj.objectId);
    });
  }

  canvas.discardActiveObject();
  canvas.requestRenderAll();
};

// create a handleKeyDown function that listen to different keydown events
export const handleKeyDown = ({
  e,
  canvas,
  undo,
  redo,
  syncShapeInStorage,
  deleteShapeFromStorage,
}: {
  e: KeyboardEvent;
  canvas: fabric.Canvas | any;
  undo: () => void;
  redo: () => void;
  syncShapeInStorage: (shape: fabric.Object) => void;
  deleteShapeFromStorage: (id: string) => void;
}) => {
  // Check if the active element is an input, textarea, or contenteditable element.
  // for overlay forms and modal inputs control, we don't want to trigger canvas copy pasting or
  // other keys.
  const activeElement: any = document.activeElement;
  const isInputField =
    activeElement?.tagName === "INPUT" ||
    activeElement?.tagName === "TEXTAREA" ||
    activeElement?.isContentEditable;

  // If an input field is active, do nothing (let the browser handle the event normally)
  if (isInputField) return;

  // Check if the key pressed is ctrl/cmd + c (copy)
  if ((e?.ctrlKey || e?.metaKey) && e.keyCode === 67) {
    handleCopy(canvas);
  }

  // Check if the key pressed is ctrl/cmd + v (paste)
  if ((e?.ctrlKey || e?.metaKey) && e.keyCode === 86) {
    handlePaste(canvas, syncShapeInStorage);
  }

  // check if the key pressed is ctrl/cmd + x (cut)
  if ((e?.ctrlKey || e?.metaKey) && e.keyCode === 88) {
    handleCopy(canvas);
    handleDelete(canvas, deleteShapeFromStorage);
  }

  // check if the key pressed is ctrl/cmd + y (redo)
  if ((e?.ctrlKey || e?.metaKey) && e.shiftKey && e.keyCode === 90) {
    console.log("Redo key called");
    return redo();
  }

  // // check if the key pressed is ctrl/cmd + z (undo)
  if ((e?.ctrlKey || e?.metaKey) && e.keyCode === 90) {
    console.log("Undo key called");
    return undo();
  }

  // if (e.keyCode === 191 && !e.shiftKey) {
  //   e.preventDefault();
  // }
};
